'use client';

import { BaseSubmit } from '@/components/Form/base';
import { LayoutContext } from '@/contexts/LayoutContext';
import { IComponentGenericFormField } from '@/types/cms';
import { useContext } from 'react';
import { twMerge } from 'tailwind-merge';
import { IGenericInquiryFormProps } from '../types';
import { BaseField, BaseMessages, BaseSMSField, BaseSubscribeField, BaseTermsField } from './Base';

// --

const styles = {
  container: 'leading-none grow relative',
  label:
    'absolute left-3 top-5 pointer-events-none leading-none text-black-400 text-sm md:text-md text-left [&>span]:text-md [&>span]:leading-none transition-all group-focus-within:left-2 group-focus-within:top-2 group-focus-within:-translate-y-0 group-focus-within:text-xs transition-all',
  labelWithValue: 'left-2 top-2 -translate-y-0 text-xs',
  input:
    'leading-none px-3 py-5 rounded-md focus:outline-none focus:ring-0 focus:border-black-500 focus:px-3 focus:pt-8 focus:pb-2',
  inputWithValue: 'px-3 pt-8 pb-2',
};

// --

interface IStyleSimpleFormProps extends IGenericInquiryFormProps {
  fieldsMapped: {
    [field: string]: IComponentGenericFormField;
  };
  wasSuccessful: boolean;
  wasError: boolean;
}

export const StyleClassicForm = (props: IStyleSimpleFormProps) => {
  const { context } = useContext(LayoutContext);
  const fieldsList = Object.values(props.fieldsMapped);
  const fieldsSize = fieldsList.length;

  return (
    <div
      className={twMerge(
        'w-full flex flex-col gap-4 text-center justify-center',
        !context.isEmbed &&
          'md:w-[720px] xl:w-[865px] mx-auto p-2 md:p-8 bg-white md:border border-black-300 rounded-lg',
        props.classes,
      )}
    >
      {props.title && (
        <div className={twMerge('font-bold text-[34px] leading-none', props.classes_title)}>{props.title}</div>
      )}
      {props.description && (
        <div className={twMerge('text-[14px] leading-none', props.classes_description)}>{props.description}</div>
      )}
      <div className="relative w-full mx-auto mt-4 grid grid-cols-2 gap-4 items-start">
        {fieldsList.map((item, index) => {
          const isExpanded =
            (index == fieldsSize - 1 && (index + 1) % 2 > 0) || // Last item and odd index
            ((index + 1) % 2 > 0 && fieldsList[index + 1]?.type === 'comments'); // Odd index and Next item is comments

          return (
            <BaseField
              key={item.type}
              {...item}
              styleClasses={styles}
              inputClasses={{
                comments: {
                  label: 'top-3 translate-y-0',
                },
              }}
              extraClasses={isExpanded ? 'col-span-2' : ''}
              locationsOptionSet={props.locationsOptionSet}
              productsListBase={props.products_list}
            />
          );
        })}
      </div>
      <div className="relative flex flex-col w-full mx-auto mt-2">
        {props.display_terms_checkbox && <BaseTermsField {...props} />}
        {props.display_subscribe_checkbox && !!fieldsList.find((item) => item.type === 'email') && (
          <BaseSubscribeField {...props} />
        )}
        {props.display_sms_checkbox && !!fieldsList.find((item) => item.type === 'phone') && (
          <BaseSMSField {...props} />
        )}
      </div>
      {(props.wasSuccessful || props.wasError) && (
        <div className="relative w-full mx-auto">
          <BaseMessages {...props} />
        </div>
      )}
      {!props.wasSuccessful && (
        <div className="relative w-full mx-auto">
          <BaseSubmit
            formNameSlug="generic-inquiry-classic"
            styleClasses="bg-blue-500 text-white rounded-sm text-[16px] py-3 px-3"
            extraClasses="w-full"
          />
        </div>
      )}
    </div>
  );
};
